import { apiClient } from '@/api/core';
import { API_CONFIG } from '@/api/core/apiConfig';
import { buildQueryString } from '@/api/shared/queryBuilder';
import type { ApiResponse } from '@/api/shared';
import type {
  LC_CheckInLocationListOut,
  CheckInLocationListParams,
  CheckInLocationPage,
  LC_CreateCheckInLocationIn,
  LC_CreateCheckInLocationOut,
  CreateCheckInLocationParams,
  LC_UpdateCheckInLocationIn,
  UpdateCheckInLocationParams,
  LC_GeolocationListOut,
  LC_GeolocationRecord,
  LC_CreateGeolocationIn,
  LC_GenerateGeolocationIn,
  LC_GenerateGeolocalizationPointsIn,
  LC_GenerateGeolocalizationPointsOut,
  GeolocationRecord,
  GeolocalizationData,
  LC_BulkDeleteLocationsIn,
  LC_BulkDeleteLocationsOut,
} from './checkInLocation.types';
import { mapCheckInLocationPageToModel, mapGeolocationListToModel, mapGeolocationRecordToModel } from './checkInLocation.mapper';


export class CheckInLocationService {
  async list(
    params: CheckInLocationListParams,
    signal?: AbortSignal,
  ): Promise<ApiResponse<CheckInLocationPage>> {
    const queryString = buildQueryString({
      page: params.page,
      pageSize: params.pageSize,
      searchText: params.searchText,
      isActive: params.isActive ?? undefined,
      hasGeolocation: params.hasGeolocation ?? undefined,
      isRemoteWork: params.isRemoteWork ?? undefined,
      orderBy: params.sort?.key,
      orderDirection: params.sort?.direction?.toUpperCase(),
    });

    const response = await apiClient.get<LC_CheckInLocationListOut>(
      `${API_CONFIG.ENDPOINTS.CHECK_IN_LOCATION.LIST}${queryString}`,
      { signal },
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: mapCheckInLocationPageToModel(response.data),
      };
    }

    return { success: false, data: null, error: response.error };
  }

  async create(
    params: CreateCheckInLocationParams,
  ): Promise<ApiResponse<LC_CreateCheckInLocationOut>> {
    const body: LC_CreateCheckInLocationIn = {
      name: params.name,
      address: params.address,
      communeId: params.communeId,
      isRemoteWork: params.isRemoteWork,
    };

    const response = await apiClient.post<LC_CreateCheckInLocationOut>(
      API_CONFIG.ENDPOINTS.CHECK_IN_LOCATION.CREATE,
      body,
    );

    if (response.success && response.data) {
      return { success: true, data: response.data };
    }

    return { success: false, data: null, error: response.error, status: response.status };
  }

  async update(params: UpdateCheckInLocationParams): Promise<ApiResponse<null>> {
    const body: LC_UpdateCheckInLocationIn = {
      name: params.name,
      address: params.address,
      communeId: params.communeId,
      isRemoteWork: params.isRemoteWork,
    };

    const response = await apiClient.put<null>(
      API_CONFIG.ENDPOINTS.CHECK_IN_LOCATION.UPDATE(params.id),
      body,
    );

    if (response.success) {
      return { success: true, data: null };
    }

    return { success: false, data: null, error: response.error, status: response.status };
  }

  async delete(id: number): Promise<ApiResponse<null>> {
    const response = await apiClient.delete<null>(
      API_CONFIG.ENDPOINTS.CHECK_IN_LOCATION.DELETE(id),
    );

    if (response.success) {
      return { success: true, data: null };
    }

    return { success: false, data: null, error: response.error, status: response.status };
  }

  async getGeolocations(
    locationId: number,
    signal?: AbortSignal,
  ): Promise<ApiResponse<GeolocalizationData>> {
    const response = await apiClient.get<LC_GeolocationListOut>(
      API_CONFIG.ENDPOINTS.CHECK_IN_LOCATION.GEOLOCATION_LIST(locationId),
      { signal },
    );

    if (response.success && response.data) {
      return { success: true, data: mapGeolocationListToModel(response.data) };
    }

    return { success: false, data: null, error: response.error, status: response.status };
  }

  async createGeolocation(
    locationId: number,
    latitude: number,
    longitude: number,
  ): Promise<ApiResponse<GeolocationRecord>> {
    const body: LC_CreateGeolocationIn = { latitude, longitude };

    const response = await apiClient.post<LC_GeolocationRecord>(
      API_CONFIG.ENDPOINTS.CHECK_IN_LOCATION.GEOLOCATION_LIST(locationId),
      body,
    );

    if (response.success && response.data) {
      return { success: true, data: mapGeolocationRecordToModel(response.data) };
    }

    return { success: false, data: null, error: response.error, status: response.status };
  }

  async deleteGeolocation(locationId: number, geolocationId: number): Promise<ApiResponse<null>> {
    const response = await apiClient.delete<null>(
      API_CONFIG.ENDPOINTS.CHECK_IN_LOCATION.GEOLOCATION_DELETE(locationId, geolocationId),
    );

    if (response.success) {
      return { success: true, data: null };
    }

    return { success: false, data: null, error: response.error, status: response.status };
  }

  async updateGenerateGeolocation(locationId: number, generate: boolean):
    Promise<ApiResponse<null>> {
    const body: LC_GenerateGeolocationIn = { generate };

    const response = await apiClient.patch<null>(
      API_CONFIG.ENDPOINTS.CHECK_IN_LOCATION.GENERATE_GEOLOCATION(locationId),
      body,
    );

    if (response.success) {
      return { success: true, data: null };
    }

    return { success: false, data: null, error: response.error, status: response.status };
  }

  async generateGeolocalizationPoints(
    locationId: number,
    radius: number,
  ): Promise<ApiResponse<GeolocationRecord[]>> {
    const body: LC_GenerateGeolocalizationPointsIn = { radiusMeters: radius };

    const response = await apiClient.post<LC_GenerateGeolocalizationPointsOut>(
      API_CONFIG.ENDPOINTS.CHECK_IN_LOCATION.GENERATE_GEOLOCATION_POINTS(locationId),
      body,
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: (response.data.items ?? []).map(mapGeolocationRecordToModel),
      };
    }

    return { success: false, data: null, error: response.error, status: response.status };
  }

  async download(): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    const response = await apiClient.downloadBlobWithFilename(
      API_CONFIG.ENDPOINTS.CHECK_IN_LOCATION.EXPORT,
      { method: 'GET' },
    );

    if (response.success && response.data) {
      return response;
    }

    return { ...response, data: null };
  }

  async bulkDisable(
    locationIds: number[],
    disableAll?: boolean,
    filters?: {
      hasGeolocation?: boolean | null;
      isRemoteWork?: boolean | null;
      searchText?: string | null;
      excludeLocationIds?: number[];
    },
  ): Promise<ApiResponse<LC_BulkDeleteLocationsOut>> {
    const body: LC_BulkDeleteLocationsIn = disableAll
      ? {
        disableAll: true,
        hasGeolocation: filters?.hasGeolocation,
        isRemoteWork: filters?.isRemoteWork,
        searchText: filters?.searchText,
        excludeLocationIds: filters?.excludeLocationIds?.length
          ? filters.excludeLocationIds.join(',')
          : undefined,
      }
      : { locationIds: locationIds.join(',') };

    const response = await apiClient.delete<LC_BulkDeleteLocationsOut>(
      API_CONFIG.ENDPOINTS.CHECK_IN_LOCATION.BULK_DELETE,
      body,
    );

    if (response.success && response.data) {
      return { success: true, data: response.data };
    }

    return { success: false, data: null, error: response.error, status: response.status };
  }
}

export const checkInLocationService = new CheckInLocationService();
